import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import {
  UserPlus, Mail, Copy, Loader2, Trash2, RefreshCw, Users, Clock, CheckCircle2, XCircle, Link2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TeamInvites = () => {
  const { token, currentOrg } = useAuth();
  const orgId = currentOrg?.org_id;

  const [invites, setInvites] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ email: '', name: '', role: 'tier1' });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const [invRes, memRes] = await Promise.all([
        axios.get(`${API}/platform/orgs/${orgId}/invites`, { headers }),
        axios.get(`${API}/platform/orgs/${orgId}/members`, { headers }),
      ]);
      setInvites(invRes.data || []);
      setMembers(memRes.data || []);
    } catch (err) {
      toast.error('Failed to load team data');
    } finally {
      setLoading(false);
    }
  }, [orgId, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!form.email || !form.name) { toast.error('Name and email are required'); return; }
    setSending(true);
    try {
      const res = await axios.post(`${API}/platform/orgs/${orgId}/invites`, form, { headers });
      toast.success(`Invitation sent to ${form.email}`);
      if (res.data.invite_url) {
        await navigator.clipboard.writeText(res.data.invite_url).catch(() => {});
        toast.info('Invite link copied to clipboard');
      }
      setForm({ email: '', name: '', role: 'tier1' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setSending(false);
    }
  };

  const handleCancel = async (inviteId) => {
    try {
      await axios.delete(`${API}/platform/invites/${inviteId}`, { headers });
      toast.success('Invitation cancelled');
      fetchData();
    } catch (err) {
      toast.error('Failed to cancel invitation');
    }
  };

  const handleResend = async (inviteId) => {
    try {
      await axios.post(`${API}/platform/invites/${inviteId}/resend`, {}, { headers });
      toast.success('Invitation resent');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend');
    }
  };

  const copyLink = async (invite) => {
    const url = `${window.location.origin}/accept-invite?token=${invite.token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Invite link copied');
    } catch {
      toast.error('Failed to copy');
    }
  };

  const statusBadge = (status) => {
    const map = {
      pending: { variant: 'outline', icon: Clock, label: 'Pending', cls: 'text-amber-600 border-amber-300' },
      accepted: { variant: 'outline', icon: CheckCircle2, label: 'Accepted', cls: 'text-emerald-600 border-emerald-300' },
      expired: { variant: 'outline', icon: XCircle, label: 'Expired', cls: 'text-slate-500 border-slate-300' },
      cancelled: { variant: 'outline', icon: XCircle, label: 'Cancelled', cls: 'text-red-500 border-red-300' },
    };
    const s = map[status] || map.pending;
    const Icon = s.icon;
    return <Badge variant={s.variant} className={`${s.cls} gap-1`}><Icon className="h-3 w-3" />{s.label}</Badge>;
  };

  const pendingInvites = invites.filter((i) => i.status === 'pending');
  const pastInvites = invites.filter((i) => i.status !== 'pending');

  return (
    <div className="space-y-6 max-w-4xl" data-testid="team-invites-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="team-page-title">Team</h1>
          <p className="text-slate-500 text-sm mt-1">{members.length} member{members.length !== 1 ? 's' : ''} &middot; {pendingInvites.length} pending invite{pendingInvites.length !== 1 ? 's' : ''}</p>
        </div>
      </div>

      {/* Invite Form */}
      <Card data-testid="invite-form-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><UserPlus className="h-4 w-4 text-blue-600" />Invite Team Member</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleInvite} className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <Input
                data-testid="invite-name-input"
                placeholder="Name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </div>
            <div className="flex-1">
              <Input
                data-testid="invite-email-input"
                type="email"
                placeholder="Email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
              />
            </div>
            <div className="w-full sm:w-40">
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="invite-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tier1">Tier 1</SelectItem>
                  <SelectItem value="tier2">Tier 2</SelectItem>
                  <SelectItem value="team_lead">Team Lead</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" disabled={sending} data-testid="send-invite-button" className="bg-blue-600 hover:bg-blue-500">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Mail className="h-4 w-4 mr-1.5" />Send</>}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Current Members */}
      <Card data-testid="members-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><Users className="h-4 w-4 text-slate-600" />Members</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
          ) : members.length === 0 ? (
            <p className="text-slate-500 text-sm text-center py-4">No members yet</p>
          ) : (
            <div className="divide-y">
              {members.map((m) => (
                <div key={m.id || m.email} className="flex items-center justify-between py-3" data-testid={`member-row-${m.email}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{m.name || m.username}</p>
                    <p className="text-xs text-slate-500 truncate">{m.email}</p>
                  </div>
                  <Badge variant="outline" className="text-xs capitalize">{m.role?.replace('_', ' ')}</Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pending Invites */}
      {pendingInvites.length > 0 && (
        <Card data-testid="pending-invites-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Clock className="h-4 w-4 text-amber-500" />Pending Invitations</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="divide-y">
              {pendingInvites.map((inv) => (
                <div key={inv.id} className="flex items-center justify-between py-3 gap-3" data-testid={`invite-row-${inv.email}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-900 truncate">{inv.name}</p>
                    <p className="text-xs text-slate-500 truncate">{inv.email}</p>
                  </div>
                  <Badge variant="outline" className="text-xs capitalize shrink-0">{inv.role?.replace('_', ' ')}</Badge>
                  <div className="flex gap-1 shrink-0">
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => copyLink(inv)} title="Copy link">
                      <Link2 className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => handleResend(inv.id)} title="Resend email">
                      <RefreshCw className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-500 hover:text-red-600" onClick={() => handleCancel(inv.id)} title="Cancel">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Past Invites */}
      {pastInvites.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-slate-500">Invite History</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="divide-y">
              {pastInvites.map((inv) => (
                <div key={inv.id} className="flex items-center justify-between py-2.5 gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-slate-700 truncate">{inv.name} &middot; {inv.email}</p>
                  </div>
                  {statusBadge(inv.status)}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TeamInvites;
